import { useRouter } from "expo-router";
import { useState } from "react";

import { authClient } from "../auth-client";
import { trpc } from "../trpc";

export type DeleteState = "idle" | "confirming" | "busy";

interface UseDeleteAccountReturn {
  state: DeleteState;
  setState: (s: DeleteState) => void;
  confirm: string;
  setConfirm: (v: string) => void;
  error: string | null;
  setError: (v: string | null) => void;
  /** Server-side delete + client-side sign-out + redirect home. */
  confirmDelete: () => Promise<void>;
  reset: () => void;
}

// Three-state machine for the account-deletion flow: idle → confirming
// → busy. The "DELETE" literal gate is enforced server-side; this hook
// holds the textbox value + transition states.
export function useDeleteAccount(): UseDeleteAccountReturn {
  const router = useRouter();
  const [state, setState] = useState<DeleteState>("idle");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  function reset(): void {
    setState("idle");
    setConfirm("");
    setError(null);
  }

  async function confirmDelete(): Promise<void> {
    setError(null);
    setState("busy");
    try {
      await trpc.user.deleteAccount.mutate({ confirmation: "DELETE" });
      // Server cleared the session cookie + cascaded the User row.
      // Tell the auth-client to forget any cached session state too,
      // then send the user home.
      await authClient.signOut().catch(() => {
        // Already signed out server-side — swallow.
      });
      router.replace("/");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "couldn't delete account",
      );
      setState("confirming");
    }
  }

  return {
    state,
    setState,
    confirm,
    setConfirm,
    error,
    setError,
    confirmDelete,
    reset,
  };
}
